// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@ces/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@ces/App/Components/Product/ProductGridContainer";
import ProductQtyInfo, { ProductQtyInfoStyles } from "@ces/App/Components/Product/ProductQtyInfo";
import xUseWidgetBadges from "@ces/App/Hooks/xUseWidgetBadges";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { ProductAvailabilityStyles } from "@ces/Overrides/Components/ProductAvailability";
import { mergeWithCss } from "@ces/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showImageBadges?: boolean;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    showInfo?: boolean;
    maximumNumberOfImageBadges?: number;
    hideAvailabilityLink?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    containerClassName?: string;
    isPdp?: boolean;
    onChangeCompare?: (checked: boolean) => void;
    goToPageLink?: string;
    goToPageLabel?: string;
}
const mapStateToProps = (state: ApplicationState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    isAuthenticated: state.context.session.isAuthenticated,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
    imageBadgesStyles?: ProductImageBadgesStyles;
    loginActionButton?: ButtonProps;
    goToPage?: LinkPresentationProps;
    qtyInfoStyles?: ProductQtyInfoStyles;
    badgesWrapper?: InjectableCss;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showImageBadges,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    hideAvailabilityLink = false,
    showInfo = true,
    maximumNumberOfImageBadges = 5,
    productContext,
    canSeePrices,
    onAddToCartClick,
    showDisabled,
    isAuthenticated,
    containerClassName,
    isPdp,
    onChangeCompare,
    goToPageLabel,
    goToPageLink,
    showTextBadges,
    maxTextBadges,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));

    const { imageBadges, textBadges } = xUseWidgetBadges({ productContext, showImageBadges, showTextBadges });
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            <ProductGridContainer.PriceAndActions>
                {(showTextBadges || showImageBadges) && (
                    <StyledWrapper {...styles.badgesWrapper}>
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />
                        )}
                        {showImageBadges && !!imageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges}
                                extendedStyles={styles.imageBadgesStyles}
                            />
                        )}
                    </StyledWrapper>
                )}
                {showPrice && (
                    <ProductGridContainer.Price>
                        <ProductPrice
                            product={productContext}
                            showInvalidPriceMessage={true}
                            showLabel={false}
                            showPack={false}
                            extendedStyles={styles.productPriceStyles}
                            {...{ showTheDefaultUomOnly: true }}
                        />
                    </ProductGridContainer.Price>
                )}
                {canSeePrices && showAvailability && (
                    <ProductGridContainer.Availability>
                        <ProductContextAvailability
                            extendedStyles={styles.productAvailabilityStyles}
                            isProductDetailsPage={isPdp}
                            {...{ hideLink: hideAvailabilityLink }}
                        />
                    </ProductGridContainer.Availability>
                )}
                {showInfo && (
                    <ProductGridContainer.QtyInfo>
                        <ProductQtyInfo
                            product={productContext.product}
                            extendedStyles={styles.qtyInfoStyles}
                            showPlaceholder={isTabletScreen}
                        />
                    </ProductGridContainer.QtyInfo>
                )}
            </ProductGridContainer.PriceAndActions>
            {showAddToCartActions && (
                <ProductGridContainer.AddToCartActions>
                    <ProductActions
                        {...productActionsProps}
                        onAddToCartClick={onAddToCartClick}
                        showDisabled={showDisabled}
                        iconOnly={showAddToCartIcon}
                        extendedStyles={styles.productActionsStyles}
                    />
                </ProductGridContainer.AddToCartActions>
            )}
            {canSeePrices && (showAddToList || showCompare) && (
                <ProductGridContainer.AddToList>
                    <ProductListProductImage
                        showImage={false}
                        showCompare={showCompare}
                        extendedStyles={styles.productListProductImageStyles}
                    />
                    {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                </ProductGridContainer.AddToList>
            )}
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
